import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { PasswordValidators } from '../../../../shared/components/validators/password.validators';
import { DialogData } from '../../../../shared/models/dialog-data.model';

@Component({
  selector: 'app-add-database-user-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
  ],
  templateUrl: './add-database-user-dialog.component.html',
  styleUrl: './add-database-user-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDatabaseUserDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddDatabaseUserDialogComponent>);

  readonly data = inject<DialogData & { databaseId: number }>(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  form = this.fb.nonNullable.group(
    {
      username: ['', [this.nameValidator]],
      databaseId: [0, [Validators.required]],
      password: [
        '',
        [
          PasswordValidators.required(),
          PasswordValidators.complexity(),
          PasswordValidators.allowedCharacters(),
          PasswordValidators.minLength(),
          PasswordValidators.maxLength(),
        ],
      ],
      passwordConf: ['', [PasswordValidators.required()]],
    },
    {
      validators: [PasswordValidators.match('password', 'passwordConf')],
    },
  );

  ngOnInit(): void {
    this.form.controls.databaseId.setValue(this.data.databaseId);
  }

  submit(): void {
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.getRawValue());
  }

  close(): void {
    this.dialogRef.close();
  }

  private nameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value ?? '';

    if (!value) {
      return {
        required: 'Name is required',
      };
    }

    if (value.length > 24) {
      return {
        maxLength: 'Name can be at most 24 characters',
      };
    }

    if (value.length < 3) {
      return {
        minLength: 'Name has to be at least 3 characters',
      };
    }

    if (!/^[a-z0-9_]+$/.test(value)) {
      return {
        invalidCharacters: 'Name must contain only letters and numbers',
      };
    }

    return null;
  }
}
